import { of as observableOf, Observable } from "rxjs";
import { OnInit, Component, OnDestroy } from "@angular/core";
import { select } from "@angular-redux/store";
import { Column, ApplicationUser } from "../../../../../pb-screen/models/application.user.interface";
import {
  SelectableItemDefinition,
  TranslatedSelectableItemDefinition,
  OnItemSelectedEvent,
  OnItemDeselectedEvent
} from "../../../../../pb-screen/containers/filters/selectable-item/selectable-item.interface";
import { ApplicationUserActions } from "../../../../../pb-screen/application.user.actions";
import { TranslateService } from "@ngx-translate/core";
import { PbActions } from "../../../../pb.actions";
import {
  OptionalPbOrderLineColumnDefiniton,
  DefaultPbOrderLineColumnDefiniton
} from "../../../../static/columns/PbOrderLineColumnDefinition";
import { Subject, Subscription } from "rxjs/Rx";
import { takeUntil } from "rxjs/operators";

@Component({
  moduleId: module.id,
  selector: "pb-orderline-column-selection",
  templateUrl: "./pb-details-column-selection.component.html",
})
export class PbOrderLineColumnSelection implements OnInit, OnDestroy {

  @select("sessionPbOrderLineColumnSelection") public sessionPbOrderLineColumnSelection$: Observable<Column[]>;
  readonly defaultColumns: Column[] = DefaultPbOrderLineColumnDefiniton;
  readonly optionalColumns: Column[] = OptionalPbOrderLineColumnDefiniton;
  selectableColumnsSet1: TranslatedSelectableItemDefinition[];
  selectableColumnsSet2: TranslatedSelectableItemDefinition[];
  selectableColumnsSet3: TranslatedSelectableItemDefinition[];
  selectableColumnsSet4: TranslatedSelectableItemDefinition[];
  selectableColumnsSet5: TranslatedSelectableItemDefinition[];
  defaultColumnDefinitions: TranslatedSelectableItemDefinition[];
  private translatedOptionalColumns: TranslatedSelectableItemDefinition[];
  private applicationUser: ApplicationUser;
  private currentSelectedColumns: Column[];
  destroy$ = new Subject();
  @select("applicationUser") applicationUser$: Observable<ApplicationUser>;


  constructor(
    private actions: PbActions,
    private userActions: ApplicationUserActions,
    private translate: TranslateService
  ) {
    this.translate.onLangChange
      .pipe(
        takeUntil(this.destroy$)
      ).subscribe(_ => {
      this.setUp();
    });
  }

  ngOnInit(): void {
    this.setUp();

    this.applicationUser$.subscribe((applicationUser: ApplicationUser) => {
      if (applicationUser) {
        this.applicationUser = applicationUser;
        let selectedColumn = applicationUser.orderlinePreferences.PbOrderLineColumnSelection;
        if (selectedColumn && selectedColumn.length > 0) {
          this.checkIfColumnNotExistInDb(selectedColumn);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setUp() {
    this.sessionPbOrderLineColumnSelection$.subscribe((selectedColumns: Column[]) => {
      if (selectedColumns) {
        this.currentSelectedColumns = selectedColumns;
      } else {
        this.currentSelectedColumns = [];
      }
    });

    const d = this.defaultColumns;
    Promise.all(d.map(async (column):
    Promise<TranslatedSelectableItemDefinition> => {
      const t = await this.translate.get(column.name).toPromise();
      return {
        enabled: false,
        checked: new Observable(o => o.next(true)),
        name: column.name,
        translated: t
      } as TranslatedSelectableItemDefinition;
    })).then((columnDefinitions) => {
      this.defaultColumnDefinitions = columnDefinitions;
      this.defaultColumnDefinitions.sort(PbOrderLineColumnSelection.sortTranslatedSelectableItemDefinition);
    });

    const o = this.optionalColumns;
    Promise.all(o.map(async (column): Promise<TranslatedSelectableItemDefinition> => {
      const t = await this.translate.get(column.name).toPromise();
      const definition = { name: column.name, translated: t, enabled: true } as TranslatedSelectableItemDefinition;
      this.sessionPbOrderLineColumnSelection$.subscribe(a => {
        definition.checked = a && a.length > 0 ? new Observable(o => o.next(a.findIndex(e => e.name === column.name) >= 0)) : observableOf(false);
      });
      return definition;
    })).then((columnDefinitions) => {
      this.translatedOptionalColumns = columnDefinitions;


      const maxSetElements = Math.ceil(this.optionalColumns.length / 5);
      const set2Ceiling = maxSetElements * 2;
      const set3Ceiling = maxSetElements * 3;
      const set4Ceiling = maxSetElements * 4;
      const set5Ceiling = maxSetElements * 5;

      this.translatedOptionalColumns.sort(PbOrderLineColumnSelection.sortTranslatedSelectableItemDefinition);

      this.selectableColumnsSet1 = this.translatedOptionalColumns.slice(0, maxSetElements);
      this.selectableColumnsSet2 = this.translatedOptionalColumns.slice(maxSetElements, set2Ceiling);
      this.selectableColumnsSet3 = this.translatedOptionalColumns.slice(set2Ceiling, set3Ceiling);
      this.selectableColumnsSet4 = this.translatedOptionalColumns.slice(set3Ceiling, set4Ceiling);
      this.selectableColumnsSet5 = this.translatedOptionalColumns.slice(set4Ceiling, set5Ceiling);
    });
  }

  checkIfColumnNotExistInDb(selection) {
    selection.forEach(Column => {
      let length = OptionalPbOrderLineColumnDefiniton.filter(c => c.name === Column.name).length;
      if (length === 0) {
        // Delete Column From List of user selected Columns
        let ind = selection.indexOf(Column);
        if (ind !== -1) {
          this.applicationUser.orderlinePreferences.PbOrderLineColumnSelection = [...[
            ...selection.slice(0, ind),
            ...selection.slice(ind + 1)
          ]];
        }
      }
    });

  }

  userSelectedColumn(event: OnItemSelectedEvent) {
    let column: Column = {
      name: event.name,
      width: 200,
      locked: false
    };
    this.actions.updatePbOrderLineSessionColumns([...this.currentSelectedColumns, column]);
  }

  userDeselectedColumn(event: OnItemDeselectedEvent) {
    const index = this.currentSelectedColumns.indexOf(this.currentSelectedColumns.filter((x) => x.name === event.name)[0]);

    if (index !== -1) {
      this.actions.updatePbOrderLineSessionColumns([
        ...this.currentSelectedColumns.slice(0, index),
        ...this.currentSelectedColumns.slice(index + 1)
      ]);
    }
  }

  private static sortTranslatedSelectableItemDefinition(item1: TranslatedSelectableItemDefinition,
                                                        item2: TranslatedSelectableItemDefinition): number {
    if (item1.translated > item2.translated) {
      return 1;
    }
    if (item2.translated > item1.translated) {
      return -1;
    }
    return 0;
  }

  saveProfile() {
    this.applicationUser.orderlinePreferences.PbOrderLineColumnSelection = [...this.currentSelectedColumns];
    this.checkIfColumnNotExistInDb(this.currentSelectedColumns);
    this.userActions.updateApplicationUser(this.applicationUser, "defaultOrderLineUserSettings", true);
  }
}
